import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/common";
import { categories, QUESTION_TYPES } from "../utils/survey-manager.helper";
import { useTran } from "@/hooks";
import { Search, Star, Archive, ArrowUpDown } from "lucide-react";

const SORT_OPTIONS = [
  { value: "recently_added", label: "Recently added" },
  { value: "oldest", label: "Oldest first" },
  { value: "alphabetical", label: "Alphabetical" },
];

const QuestionSearchFilter = ({ filters, onFilterChange }) => {
  const { t } = useTran();

  // ✅ "All categories" is first — value:"all" matches store default
  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All categories" },
      ...categories.map((c) => ({
        value: c,
        label: c.charAt(0).toUpperCase() + c.slice(1),
      })),
    ],
    [],
  );

  // ✅ "All types" is first — value:"all" matches store default
  const questionTypeOptions = useMemo(
    () => [
      { value: "all", label: "All types" },
      ...QUESTION_TYPES.map((type) => ({
        value: type,
        label: type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      })),
    ],
    [],
  );

  const set = (key, value) => onFilterChange(key, value);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* ── Search ── */}
      <div className="relative w-60">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2
                                   h-4 w-4 text-muted-foreground pointer-events-none"
        />
        <Input
          className="pl-9 h-9 text-sm"
          placeholder={t("Search questions...")}
          value={filters.search} // ✅ from filters object
          onChange={(e) => set("search", e.target.value)}
        />
      </div>

      {/* ── Category — defaults to "All categories" when filters.category === "all" ── */}
      <SelectField
        value={filters.category} // ✅ "all" → selects first option
        onChange={(e) => set("category", e.target.value)}
        options={categoryOptions}
        className="h-9 text-sm w-40"
      />

      {/* ── Type — defaults to "All types" when filters.type === "all" ── */}
      <SelectField
        value={filters.type} // ✅ "all" → selects first option
        onChange={(e) => set("type", e.target.value)}
        options={questionTypeOptions}
        className="h-9 text-sm w-40"
      />

      {/* ── Push right ── */}
      <div className="flex-1" />

      {/* ── Favourites button ── */}
      <Button
        variant={filters.showFavourites ? "secondary" : "outline"}
        size="sm"
        onClick={() => set("showFavourites", !filters.showFavourites)}
        className={[
          "h-9 gap-1.5 transition-colors",
          filters.showFavourites
            ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
            : "text-muted-foreground hover:text-foreground",
        ].join(" ")}
      >
        <Star
          className={[
            "h-4 w-4 transition-colors",
            filters.showFavourites ? "fill-amber-400 text-amber-400" : "",
          ].join(" ")}
        />
        {t("Favourites")}
      </Button>

      {/* ── Archived button ── */}
      <Button
        variant={filters.showArchived ? "secondary" : "outline"}
        size="sm"
        onClick={() => set("showArchived", !filters.showArchived)}
        className={[
          "h-9 gap-1.5 transition-colors",
          filters.showArchived
            ? "border-slate-400 bg-slate-100 text-slate-700 hover:bg-slate-200"
            : "text-muted-foreground hover:text-foreground",
        ].join(" ")}
      >
        <Archive
          className={[
            "h-4 w-4 transition-colors",
            filters.showArchived ? "text-slate-600" : "",
          ].join(" ")}
        />
        {t("Archived")}
      </Button>

      {/* ── Sort ── */}
      <SelectField
        value={filters.sort} // ✅ from filters object
        onChange={(e) => set("sort", e.target.value)}
        options={SORT_OPTIONS}
        className="h-9 text-sm w-44"
        icon={<ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />}
      />
    </div>
  );
};

export default QuestionSearchFilter;
